/* eslint-disable react/display-name */
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToFirstScrollableAncestor} from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {IconButton} from '@dropbox/dig-components/buttons';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Toggletip, Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  ArrowDownLine,
  ArrowUpLine,
  DragHandleLine,
  ExpandDownLine,
  HideLine,
  MoreHorizontalLine,
  ShowLine,
  UploadLine,
} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import cx from 'classnames';
import {t} from 'i18next';
import {atom, useAtomValue} from 'jotai';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';

import styles from './Table.module.css';
import {
  readMainOffsetLeftPx,
  readStickyOffsetTopPx,
  TABLE_PAGE_SCROLL_FRAME_Z_INDEX,
  tablePageScrollStickyDataProps,
  tableStickyScrollContainerDataProps,
} from './tableStickyLayout';

const MAX_WIDTH = 1240;
const MIN_WIDTH = 50;

export const MAX_COMPACT_HEIGHT = 206;

const COMPACT_ACTION_BUTTON_SIZE = 24;
const COMPACT_ACTION_GAP = 4;
const COMPACT_ACTION_OFFSET = 12;
const COMPACT_ACTION_BUFFER = 4;

interface BaseColumn<T> {
  type: string;
  tooltip?: ReactNode;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  sort?: (a: T, b: T) => number;
  fixed?: boolean;
  maxWidth?: number;
  width?: number;
  spanner?: boolean;
  cantDisable?: boolean;
  skeleton?: () => React.ReactElement;
}

export type Column<T> = BaseColumn<T> &
  ({fixed: boolean; minWidth?: number} | {fixed?: undefined; minWidth: number});

interface SortConfig {
  columnId: string;
  asc: boolean;
}

interface TableContextValue<T> {
  bodyCellPadding?: number;
  columns: Column<T>[];
  compact?: boolean;
  headerTextSize?: React.ComponentProps<typeof Text>['size'];
  hiddenColumns?: string[];
  toggleColumnVisibility?: (column: string) => void;
  sortedData: T[];
  sortConfig: SortConfig | null;
  padding?: boolean;
  onSort: (col: Column<T>) => void;
  reorderable: boolean;
  resizable: boolean;
  onResizeStart: (index: number, e: React.MouseEvent) => void;
  draggingCol: number | null;
  isReordering: boolean;
  onReorderingStart?: () => void;
  onReorderingEnd?: () => void;
  columnLabelOverrides?: Record<string, string>;
  /** When set, replaces the default bold header label for that column (e.g. planning comment fields). */
  columnHeaderLabel?: Record<string, ReactNode>;
  columnHeaderContent?: Record<string, ReactNode>;
  lastColumnResizeHandle: boolean;
  columnSizing: 'share' | 'content';
}

const TableContext = createContext<TableContextValue<any> | null>(null);

export const resetColumnsAtom = atom(0);

interface TableProps<T> {
  bodyCellPadding?: number;
  columns: Column<T>[];
  data: T[];
  compact?: boolean;
  headerTextSize?: React.ComponentProps<typeof Text>['size'];
  padding?: boolean;
  border?: boolean;
  borderRadius?: 'Medium' | 'Large';
  children: ReactNode;
  verticalLines?: boolean;
  horizontalLines?: boolean;
  reorderable?: boolean;
  /** Whether column resize handles are shown. */
  resizable?: boolean;
  sortConfig?: SortConfig;
  onReorder?: (event: DragEndEvent) => void;
  isReordering?: boolean;
  onReorderingStart?: () => void;
  onReorderingEnd?: () => void;
  hiddenColumns?: string[];
  toggleColumnVisibility?: (column: string) => void;
  columnLabelOverrides?: Record<string, string>;
  columnHeaderLabel?: Record<string, ReactNode>;
  columnHeaderContent?: Record<string, ReactNode>;
  onColumnResizeEnd?: (columnType: string, width: number) => void;
  onGridTemplateColumnsChange?: (gridTemplateColumns: string) => void;
  lastColumnResizeHandle?: boolean;
  /** Accessible name for a keyboard-scrollable self-scrolling table region. */
  scrollAreaLabel?: string;
  /**
   * How the table scrolls and where its headers stick:
   * - `self` (default): the table wrapper scrolls horizontally on its own; edge fades follow scrollLeft.
   *   Headers are not sticky (used by admin/dense tables).
   * - `page`: the table overflows to the page (no inner scroll container), column headers stick to
   *   the fixed page chrome via `--dsys-sticky-offset-top`, and edge fades are pinned to the viewport.
   */
  scrollMode?: 'self' | 'page';
  /**
   * `self` scroll mode only: stop the elastic bounce (and swipe back/forward) at the
   * table's left and right edges.
   */
  disableHorizontalOverscroll?: boolean;
  /**
   * How unresized columns share the width:
   * - `share` (default): each column takes an equal share, floored at its `minWidth`.
   * - `content`: each column sizes to its cells, like an HTML table, and any spare room is
   *   spread across them. Cap or floor a column by constraining its cell content.
   */
  columnSizing?: 'share' | 'content';
}

// Create a type that adds the id requirement only when reorderable is true
type TablePropsWithReorder<T, R extends boolean | undefined> = Omit<
  TableProps<T>,
  'data' | 'reorderable'
> & {
  data: R extends true ? (T & {id: number})[] : T[];
  reorderable?: R;
};

const TableComponent = <T,>({
  columns,
  data,
  children,
  bodyCellPadding,
  verticalLines = true,
  horizontalLines = true,
  border = true,
  borderRadius = 'Medium',
  padding = true,
  compact = false,
  headerTextSize = 'medium',
  className,
  style,
  reorderable = false,
  resizable = true,
  sortConfig: initialSortConfig,
  onReorder,
  isReordering = false,
  onReorderingStart,
  onReorderingEnd,
  hiddenColumns,
  toggleColumnVisibility,
  columnLabelOverrides,
  columnHeaderLabel,
  columnHeaderContent,
  onColumnResizeEnd,
  onGridTemplateColumnsChange,
  lastColumnResizeHandle = false,
  scrollAreaLabel,
  scrollMode = 'self',
  disableHorizontalOverscroll = false,
  columnSizing = 'share',
}: React.HTMLAttributes<HTMLDivElement> & TablePropsWithReorder<T, boolean>) => {
  const processedHiddenColumns = useMemo(() => hiddenColumns, [hiddenColumns]);
  const resetColumnsTrigger = useAtomValue(resetColumnsAtom);

  const [sortConfig, setSortConfig] = useState<SortConfig | null>(initialSortConfig ?? null);
  const [draggingCol, setDraggingCol] = useState<number | null>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const [shadowBoundsStyle, setShadowBoundsStyle] = useState<React.CSSProperties>({});
  // Fixed-position edge shadows for page-scroll tables, pinned to the visible viewport edges.
  const [pageScrollShadowBounds, setPageScrollShadowBounds] = useState<{
    left: number;
    top: number;
    height: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const resizeDidMoveRef = useRef(false);
  const previousPointerXRef = useRef<number | null>(null);
  // Cached page-scroll layout offsets (refreshed on resize, not per scroll frame).
  const chromeBottomRef = useRef(52);
  const contentLeftRef = useRef(56);

  // Store each column's width in px, or 0 if it's unresized (i.e., still "1fr").
  const [colWidths, setColWidths] = useState<number[]>(() =>
    columns.map((col) => {
      if (col.fixed) {
        // For fixed columns, use minWidth if specified, otherwise use -1 to indicate min-content
        return col.minWidth ?? -1;
      }
      if (col.width) return col.width;
      return 0; // still "1fr" if no width specified
    })
  );

  // Keep the internal width model in sync before paint so header/body don't briefly
  // render against the previous column set when columns are swapped.
  useLayoutEffect(() => {
    setColWidths(
      columns.map((col) => {
        if (col.fixed) {
          // For fixed columns, use minWidth if specified, otherwise use -1 to indicate min-content
          return col.minWidth ?? -1;
        }
        if (col.width) return col.width;
        return 0; // still "1fr" if no width specified
      })
    );
  }, [columns, resetColumnsTrigger]);

  const isPageScrollTableOverflow = scrollMode === 'page';

  const handleScroll = useCallback(() => {
    // Page-scroll tables overflow to the page; the table grid keeps a normal box at
    // container width while its tracks overflow, so detect horizontal clipping from
    // the grid's viewport rect + scrollWidth and pin fixed shadows to the visible edges.
    if (isPageScrollTableOverflow) {
      const grid = scrollRef.current;
      if (!grid || typeof window === 'undefined') {
        setShowLeftShadow(false);
        setShowRightShadow(false);
        return;
      }

      const rect = grid.getBoundingClientRect();
      const chromeBottom = chromeBottomRef.current;
      const contentLeft = contentLeftRef.current;
      const viewportRight = window.innerWidth;
      const contentRightInViewport = rect.left + grid.scrollWidth;

      const top = Math.max(chromeBottom, rect.top);
      const bottom = Math.min(window.innerHeight, rect.bottom);
      const height = Math.max(0, bottom - top);
      const inView = height > 0;

      setShowLeftShadow(inView && rect.left < contentLeft - 1);
      setShowRightShadow(inView && contentRightInViewport > viewportRight + 1);
      setPageScrollShadowBounds((previous) => {
        const next = {
          left: Math.round(contentLeft),
          top: Math.round(top),
          height: Math.round(height),
        };
        return previous &&
          previous.left === next.left &&
          previous.top === next.top &&
          previous.height === next.height
          ? previous
          : next;
      });
      return;
    }

    const tableWrapper = scrollRef.current;
    if (!tableWrapper) {
      return;
    }

    const {scrollLeft, scrollWidth, clientWidth} = tableWrapper;
    setShowLeftShadow(scrollLeft > 1);
    setShowRightShadow(scrollWidth - clientWidth - scrollLeft > 1);
  }, [isPageScrollTableOverflow]);

  // Initial + window resize; rAF catches one frame after layout/fonts so scrollWidth matches the grid.
  useEffect(() => {
    handleScroll();
    const raf = requestAnimationFrame(() => handleScroll());
    window.addEventListener('resize', handleScroll);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleScroll]);

  const syncShadowBounds = useCallback(() => {
    const containerElement = containerRef.current;
    if (!containerElement) {
      return;
    }

    if (isPageScrollTableOverflow) {
      return;
    }

    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    const containerRect = containerElement.getBoundingClientRect();
    const scrollRect = scrollElement.getBoundingClientRect();
    if (
      !containerRect ||
      !scrollRect ||
      typeof containerRect.top !== 'number' ||
      typeof containerRect.bottom !== 'number' ||
      typeof scrollRect.top !== 'number' ||
      typeof scrollRect.bottom !== 'number'
    ) {
      return;
    }

    const nextTop = Math.max(0, scrollRect.top - containerRect.top);
    const nextBottom = Math.max(0, containerRect.bottom - scrollRect.bottom);
    const nextShadowBoundsStyle = {
      top: `${Math.round(nextTop)}px`,
      bottom: `${Math.round(nextBottom)}px`,
    } satisfies React.CSSProperties;

    setShadowBoundsStyle((previousStyle) =>
      previousStyle.top === nextShadowBoundsStyle.top &&
      previousStyle.bottom === nextShadowBoundsStyle.bottom
        ? previousStyle
        : nextShadowBoundsStyle
    );
  }, [isPageScrollTableOverflow]);

  useLayoutEffect(() => {
    // Refresh cached layout offsets (probes the DOM); only on layout/resize, never per scroll.
    const syncScrollShadows = () => {
      if (isPageScrollTableOverflow) {
        chromeBottomRef.current = readStickyOffsetTopPx();
        contentLeftRef.current = readMainOffsetLeftPx();
      }
      syncShadowBounds();
      handleScroll();
    };

    syncScrollShadows();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(syncScrollShadows);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      if (scrollRef.current) {
        resizeObserver.observe(scrollRef.current);
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', syncScrollShadows, {passive: true});
      // Page-scroll tables move with the document, so track window scroll to keep the
      // fixed edge shadows pinned to the table's visible left/right edges. Scroll uses the
      // cached offsets (handleScroll) to avoid DOM probing every frame.
      if (isPageScrollTableOverflow) {
        window.addEventListener('scroll', handleScroll, {passive: true});
      }
    }

    return () => {
      resizeObserver?.disconnect();
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', syncScrollShadows);
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [syncShadowBounds, handleScroll, isPageScrollTableOverflow]);

  // Capture the actual rendered width when first grabbing the handle
  const onResizeStart = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.stopPropagation();

      const column = columns[index];

      analyticsLogger().logEvent('TABLE_COLUMN_RESIZE', {
        type: column.type,
        fixed: column.fixed,
        width: column.width,
        minWidth: column.minWidth,
        maxWidth: column.maxWidth,
      });

      if (column.fixed) return; // can't resize fixed columns

      // If this column is still at 0 => "1fr", we grab the actual DOM width
      if (colWidths.some((w) => w === 0)) {
        const rightmostVisibleIndex = columns.reduceRight((lastVisibleIndex, currentColumn, i) => {
          if (lastVisibleIndex !== -1) return lastVisibleIndex;
          return processedHiddenColumns?.includes(currentColumn.type) ? -1 : i;
        }, -1);
        const flexibleFillColumnIndex = rightmostVisibleIndex;

        // measure all columns still at 0
        const headerCells = document.querySelectorAll(`.${styles.headerCell}`);
        headerCells.forEach((cell, i) => {
          if (colWidths[i] === 0) {
            if (i !== index && i === flexibleFillColumnIndex) {
              return;
            }
            const rect = cell.getBoundingClientRect();
            if (!rect || typeof rect.width !== 'number') return;
            setColWidths((prev) => {
              const newWidths = [...prev];
              newWidths[i] = rect.width;
              return newWidths;
            });
          }
        });
      }

      resizeDidMoveRef.current = false;
      previousPointerXRef.current = Number.isFinite(e.clientX) ? e.clientX : null;
      setDraggingCol(index);
    },
    [columns, colWidths, processedHiddenColumns]
  );

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (draggingCol == null) return;

      handleScroll();

      setColWidths((prev) => {
        const next = [...prev];
        const c = columns[draggingCol];
        // Skip resizing fixed columns
        if (c.fixed) return prev;

        const pointerDelta =
          Number.isFinite(e.movementX) && e.movementX !== 0
            ? e.movementX
            : previousPointerXRef.current != null && Number.isFinite(e.clientX)
              ? e.clientX - previousPointerXRef.current
              : 0;
        if (Number.isFinite(e.clientX)) {
          previousPointerXRef.current = e.clientX;
        }

        // Add pointer movement to the current width
        const newVal = next[draggingCol] + pointerDelta;
        const min = c.minWidth || MIN_WIDTH;
        // Apply both min and max constraints
        const max = c.maxWidth || MAX_WIDTH;
        next[draggingCol] = Math.min(max, Math.max(min, newVal));
        if (pointerDelta !== 0) {
          resizeDidMoveRef.current = true;
        }
        return next;
      });
    },
    [draggingCol, handleScroll, columns]
  );

  const onMouseUp = useCallback(() => {
    if (draggingCol === null) return;

    const column = columns[draggingCol];

    analyticsLogger().logEvent('TABLE_COLUMN_RESIZE', {
      type: column.type,
      fixed: column.fixed,
      width: column.width,
      minWidth: column.minWidth,
      maxWidth: column.maxWidth,
      newWidth: colWidths[draggingCol],
    });

    if (resizeDidMoveRef.current && onColumnResizeEnd && !column.fixed) {
      const min = column.minWidth || MIN_WIDTH;
      const max = column.maxWidth || MAX_WIDTH;
      const resolvedWidth = Math.min(max, Math.max(min, colWidths[draggingCol]));
      onColumnResizeEnd(column.type, resolvedWidth);
    }

    resizeDidMoveRef.current = false;
    previousPointerXRef.current = null;
    setDraggingCol(null);
  }, [draggingCol, columns, colWidths, onColumnResizeEnd]);

  useEffect(() => {
    if (draggingCol !== null) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      return () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
    }
  }, [draggingCol, onMouseMove, onMouseUp]);

  // Build grid sizes
  const getColumnSize = useCallback(
    (col: Column<T>, i: number) => {
      // Hidden columns get a small fixed width for the icon
      if (processedHiddenColumns?.includes(col.type)) {
        return 'minmax(40px, 40px)';
      }

      // Content-sized columns stay `auto` until the user drags one; a resize start snapshots
      // every column's rendered width, after which the px rules below apply.
      if (columnSizing === 'content' && colWidths[i] === 0) {
        return 'auto';
      }

      // Find the rightmost visible column (not hidden)
      const rightmostVisibleIndex = columns.reduceRight((lastVisibleIndex, column, index) => {
        if (lastVisibleIndex !== -1) return lastVisibleIndex;
        return processedHiddenColumns?.includes(column.type) ? -1 : index;
      }, -1);

      // Check if this column should be the spanner
      // First, find if there's a visible spanner column
      const visibleSpannerIndex = columns.findIndex(
        (column) => column.spanner && !processedHiddenColumns?.includes(column.type)
      );

      // Check if the original spanner column has been resized (no longer acting as spanner)
      const originalSpannerResized =
        visibleSpannerIndex !== -1 && colWidths[visibleSpannerIndex] > 0;

      // Determine which column should be the spanner:
      // 1. If original spanner exists and hasn't been resized, use it
      // 2. If original spanner has been resized, use the rightmost visible column
      // 3. If no original spanner exists, use the rightmost visible column
      const shouldBeSpanner =
        (col.spanner && !processedHiddenColumns?.includes(col.type) && colWidths[i] === 0) ||
        (originalSpannerResized && i === rightmostVisibleIndex) ||
        (visibleSpannerIndex === -1 && i === rightmostVisibleIndex);

      if (shouldBeSpanner) {
        // Explicit pixel width on a spanner column: honor it (fixed track) instead of growing to 1fr.
        // Otherwise embeds / tables that pass both `spanner` and `width` never get a sensible default size.
        if (!col.fixed && colWidths[i] > 0) {
          const min = col.minWidth || MIN_WIDTH;
          const max = col.maxWidth || Infinity;
          const w = Math.min(max, Math.max(min, colWidths[i]));
          return `minmax(${w}px, ${w}px)`;
        }
        return `minmax(${col.minWidth || MIN_WIDTH}px, 1fr)`;
      }

      // Special case for fixed columns with no minWidth (using min-content)
      if (colWidths[i] === -1) {
        return 'minmax(min-content, min-content)';
      }

      if (colWidths[i] === 0) {
        if (col.minWidth) {
          // Use minmax to enforce both min and max if specified
          const max = col.maxWidth ? `${col.maxWidth}px` : '1fr';
          return `minmax(${col.minWidth}px, ${max})`;
        }
        return '1fr';
      }

      if (col.fixed) {
        // If minWidth is set, lock to that; otherwise lock to min-content
        if (col.minWidth) {
          return `minmax(${col.minWidth}px, ${col.minWidth}px)`;
        }
        return 'minmax(min-content, min-content)';
      }

      // Use the current width (either initial width or user-resized)
      const min = col.minWidth || MIN_WIDTH;
      const max = col.maxWidth || Infinity;
      const w = Math.min(max, Math.max(min, colWidths[i]));
      return `minmax(${w}px, ${w}px)`;
    },
    [colWidths, processedHiddenColumns, columns, columnSizing]
  );

  // Sorting
  const onSort = useCallback((col: Column<T>) => {
    if (!col.sort) return;
    setSortConfig((prev) => {
      if (prev?.columnId === col.type) {
        return {columnId: col.type, asc: !prev.asc};
      }
      return {columnId: col.type, asc: true};
    });
  }, []);

  const sortedData = useMemo(() => {
    if (!sortConfig) return data;
    const col = columns.find((c) => c.type === sortConfig.columnId);
    if (!col?.sort) return data;
    const sorted = [...data].sort(col.sort);
    return sortConfig.asc ? sorted : [...sorted].reverse();
  }, [data, columns, sortConfig]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const value: TableContextValue<T> = {
    bodyCellPadding,
    columns,
    compact,
    headerTextSize,
    hiddenColumns: processedHiddenColumns,
    toggleColumnVisibility,
    sortedData,
    sortConfig,
    padding,
    onSort,
    reorderable,
    resizable,
    draggingCol,
    isReordering,
    onResizeStart,
    onReorderingStart,
    onReorderingEnd,
    columnLabelOverrides,
    columnHeaderLabel,
    columnHeaderContent,
    lastColumnResizeHandle,
    columnSizing,
  };

  const gridTemplateColumns = useMemo(
    () => columns.map(getColumnSize).join(' '),
    [columns, getColumnSize]
  );

  useLayoutEffect(() => {
    if (!onGridTemplateColumnsChange) return;
    const tableElement = scrollRef.current;
    if (!tableElement) return;

    const syncResolvedGridTemplateColumns = () => {
      let nextGridTemplateColumns = gridTemplateColumns;
      if (typeof window !== 'undefined') {
        const resolvedGridTemplateColumns =
          window.getComputedStyle(tableElement).gridTemplateColumns;
        if (resolvedGridTemplateColumns && resolvedGridTemplateColumns !== 'none') {
          nextGridTemplateColumns = resolvedGridTemplateColumns;
        }
      }
      onGridTemplateColumnsChange(nextGridTemplateColumns);
    };

    syncResolvedGridTemplateColumns();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(syncResolvedGridTemplateColumns);
      resizeObserver.observe(tableElement);
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', syncResolvedGridTemplateColumns, {passive: true});
    }

    return () => {
      resizeObserver?.disconnect();
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', syncResolvedGridTemplateColumns);
      }
    };
  }, [gridTemplateColumns, onGridTemplateColumnsChange]);

  // Publish column-header height so page-scroll group headers can offset below it.
  useLayoutEffect(() => {
    const headerRoot = scrollRef.current;
    const cssVarTarget = containerRef.current ?? headerRoot;
    if (!headerRoot || !cssVarTarget) {
      return;
    }

    const publishHeaderHeight = () => {
      const headerCell = headerRoot.querySelector(`.${styles.headerCell}`);
      if (!headerCell) {
        return;
      }
      const rect = headerCell.getBoundingClientRect();
      if (!rect) {
        return;
      }
      cssVarTarget.style.setProperty('--dsys-table-header-height', `${rect.height}px`);
    };

    publishHeaderHeight();

    const headerCell = headerRoot.querySelector(`.${styles.headerCell}`);
    if (!headerCell) {
      return;
    }

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', publishHeaderHeight);
      return () => window.removeEventListener('resize', publishHeaderHeight);
    }

    const resizeObserver = new ResizeObserver(publishHeaderHeight);
    resizeObserver.observe(headerCell);
    return () => resizeObserver.disconnect();
  }, [columns, processedHiddenColumns, children, gridTemplateColumns]);

  const tableGridStyle = {
    display: 'grid',
    gridTemplateColumns,
    '--dsys-table-grid-template-columns': gridTemplateColumns,
  } as React.CSSProperties & Record<'--dsys-table-grid-template-columns', string>;

  const tableWrapperClassName = cx(styles.tableWrapper, {
    [styles.verticalLines]: verticalLines,
    [styles.horizontalLines]: horizontalLines,
  });

  const renderTableGrid = (
    gridRef: React.Ref<HTMLDivElement>,
    gridChildren: ReactNode,
    options?: {
      overflowX?: 'auto' | 'visible';
      onScroll?: () => void;
      gridStyle?: React.CSSProperties;
    }
  ) => (
    <Box
      ref={gridRef}
      position="relative"
      backgroundColor="Background Base"
      borderRadius={!isPageScrollTableOverflow && border ? (borderRadius ?? 'Medium') : undefined}
      borderStyle={!isPageScrollTableOverflow && border ? 'Solid' : undefined}
      borderColor="Border Subtle"
      className={tableWrapperClassName}
      overflowX={options?.overflowX === 'auto' ? 'auto' : undefined}
      role={options?.overflowX === 'auto' && scrollAreaLabel ? 'region' : undefined}
      aria-label={options?.overflowX === 'auto' ? scrollAreaLabel : undefined}
      tabIndex={options?.overflowX === 'auto' && scrollAreaLabel ? 0 : undefined}
      onScroll={options?.onScroll}
      style={{
        ...tableGridStyle,
        ...options?.gridStyle,
        overflowX: options?.overflowX,
      }}
    >
      {gridChildren}
    </Box>
  );

  const tableWrapperElement = isPageScrollTableOverflow ? (
    <Box
      className={cx(styles.pageScrollTableFrame, {
        // Only reserve the right-side scroll buffer when the table actually overflows
        // horizontally; otherwise a table that fits its container gets a phantom gap.
        [styles.pageScrollTableFrameScrollable]: showLeftShadow || showRightShadow,
      })}
      position="relative"
      backgroundColor={border ? 'Background Base' : undefined}
      borderStyle={border ? 'Solid' : undefined}
      borderColor={border ? 'Border Subtle' : undefined}
      borderRadius={border ? (borderRadius ?? 'Medium') : undefined}
      // Shrink-to-fit so the border wraps the full table when columns overflow,
      // while min-width keeps it full-width (flexible columns fill) when they fit.
      style={{zIndex: TABLE_PAGE_SCROLL_FRAME_Z_INDEX, width: 'fit-content', minWidth: '100%'}}
      {...tableStickyScrollContainerDataProps}
    >
      {renderTableGrid(scrollRef, children, {overflowX: 'visible'})}
    </Box>
  ) : (
    renderTableGrid(scrollRef, children, {
      overflowX: 'auto',
      onScroll: handleScroll,
      gridStyle: disableHorizontalOverscroll ? {overscrollBehaviorX: 'none'} : undefined,
    })
  );

  const tableContent = (
    <Box
      ref={containerRef}
      position="relative"
      className={className}
      style={style}
      {...(isPageScrollTableOverflow ? tablePageScrollStickyDataProps : {})}
    >
      {isPageScrollTableOverflow ? (
        <>
          {tableWrapperElement}
          {pageScrollShadowBounds ? (
            <>
              <div
                className={cx(styles.pageScrollEdge, styles.pageScrollEdgeLeft, {
                  [styles.visible]: showLeftShadow,
                })}
                style={{
                  left: pageScrollShadowBounds.left,
                  top: pageScrollShadowBounds.top,
                  height: pageScrollShadowBounds.height,
                }}
              />
              <div
                className={cx(styles.pageScrollEdge, styles.pageScrollEdgeRight, {
                  [styles.visible]: showRightShadow,
                })}
                style={{
                  right: 0,
                  top: pageScrollShadowBounds.top,
                  height: pageScrollShadowBounds.height,
                }}
              />
            </>
          ) : null}
        </>
      ) : (
        <>
          <div
            className={cx(styles.shadowOverlay, styles.leftShadow, {
              [styles.visible]: showLeftShadow,
            })}
            style={shadowBoundsStyle}
          />
          <div
            className={cx(styles.shadowOverlay, styles.rightShadow, {
              [styles.visible]: showRightShadow,
            })}
            style={shadowBoundsStyle}
          />
          {tableWrapperElement}
        </>
      )}
    </Box>
  );

  return (
    <TableContext.Provider value={value}>
      {reorderable ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onReorder}
          modifiers={[restrictToFirstScrollableAncestor]}
        >
          <SortableContext
            items={reorderable ? (data as (T & {id: number})[]).map(({id}) => id) : []}
            strategy={verticalListSortingStrategy}
          >
            {tableContent}
          </SortableContext>
        </DndContext>
      ) : (
        tableContent
      )}
    </TableContext.Provider>
  );
};

const shortenText = (text: string) => {
  const words = text.split(' ');
  if (words.length >= 2) {
    return words[0].substring(0, 1) + words[1].substring(0, 2);
  }

  return words[0].substring(0, 3);
};

// Table.Header
const Header = Object.assign(
  (props: React.HTMLAttributes<HTMLDivElement>) => {
    const [hoveredResizeColumnIndex, setHoveredResizeColumnIndex] = useState<number | null>(null);
    const ctx = useContext(TableContext);
    if (!ctx) return null;

    const {
      columns,
      sortConfig,
      onSort,
      draggingCol,
      onResizeStart,
      padding,
      hiddenColumns,
      toggleColumnVisibility,
      columnLabelOverrides,
      columnHeaderLabel,
      columnHeaderContent,
      headerTextSize,
      lastColumnResizeHandle,
      resizable,
      columnSizing,
    } = ctx;

    const getColumnLabel = (colType: string) =>
      columnLabelOverrides?.[colType] ? t(columnLabelOverrides[colType]) : t(colType);

    const visibleColumnIndices = columns
      .map((col, index) => ({col, index}))
      .filter(({col}) => !hiddenColumns?.includes(col.type));
    const firstVisibleColumnIndex = visibleColumnIndices[0]?.index ?? 0;
    const lastVisibleColumnIndex =
      visibleColumnIndices[visibleColumnIndices.length - 1]?.index ?? columns.length - 1;

    return (
      <div
        className={cx(styles.header, props.className)}
        style={{display: 'contents', ...props.style}}
        {...props}
      >
        {columns.map((col, index) => {
          const headerEdge =
            index === firstVisibleColumnIndex
              ? 'start'
              : index === lastVisibleColumnIndex
                ? 'end'
                : undefined;
          const isSorted = sortConfig?.columnId === col.type;
          const resizedColumnIndex = index - 1;
          const canRenderResizeHandle =
            resizable && index > 0 && !columns[resizedColumnIndex].fixed;
          const shouldShowResizeHandle =
            canRenderResizeHandle &&
            (hoveredResizeColumnIndex === resizedColumnIndex || draggingCol === resizedColumnIndex);

          return (
            <Text
              color="subtle"
              key={col.type}
              data-column-type={col.type}
              data-header-edge={headerEdge}
              className={cx(styles.headerCell, {
                [styles.padding]: padding,
                [styles.headerCellWithContent]: Boolean(
                  columnHeaderContent?.[col.type] || columnHeaderLabel?.[col.type]
                ),
                // A wrapped label in one column makes the whole header row taller.
                [styles.headerCellNoWrap]: columnSizing === 'content',
              })}
            >
              {hiddenColumns?.includes(col.type) ? (
                <Tooltip title={t('tooltip_show_column', {column: getColumnLabel(col.type)})}>
                  {toggleColumnVisibility ? (
                    <IconButton
                      variant="transparent"
                      size="small"
                      className={styles.minimizedColumnHeader}
                      aria-label={t('tooltip_show_column', {column: getColumnLabel(col.type)})}
                      onClick={() => toggleColumnVisibility?.(col.type)}
                    >
                      <UIIcon
                        className={cx(styles.visibilityIcon, atoms({color: 'Text Subtle'}))}
                        src={ShowLine}
                      />
                    </IconButton>
                  ) : (
                    <Text isBold style={{cursor: 'default'}}>
                      {shortenText(getColumnLabel(col.type))}
                    </Text>
                  )}
                </Tooltip>
              ) : (
                <Box
                  onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                    // Only sort if not clicking the resize handle
                    if ((e.target as HTMLElement).classList.contains(styles.resizeHandle)) {
                      return;
                    }
                    if (col.sort) onSort(col);
                  }}
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  width="100%"
                  style={{gap: 2}}
                >
                  {col.icon && (
                    <UIIcon size="small" src={col.icon} className={atoms({color: 'Text Base'})} />
                  )}
                  {columnHeaderLabel?.[col.type] ? (
                    <Box
                      flexGrow={1}
                      flexShrink={1}
                      minWidth="0"
                      onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                    >
                      {columnHeaderLabel[col.type]}
                    </Box>
                  ) : (
                    <Text isBold size={headerTextSize}>
                      {getColumnLabel(col.type)}
                    </Text>
                  )}
                  {col.tooltip && (
                    <Box display="inline-flex" flexShrink={0}>
                      <Toggletip title={col.tooltip} placement="top-end" />
                    </Box>
                  )}
                  {columnHeaderContent?.[col.type] ? (
                    <Box
                      marginLeft="auto"
                      flexGrow={1}
                      flexShrink={1}
                      minWidth="0"
                      onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                    >
                      {columnHeaderContent[col.type]}
                    </Box>
                  ) : null}
                  {!(hiddenColumns === undefined || col.cantDisable) && (
                    <Tooltip title={t('tooltip_hide_column')}>
                      <IconButton
                        className={styles.hideIcon}
                        variant="transparent"
                        size="small"
                        onClick={() => toggleColumnVisibility?.(col.type)}
                      >
                        <UIIcon src={HideLine} className={atoms({color: 'Text Subtle'})} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isSorted && (
                    <IconButton variant="transparent" size="small">
                      <UIIcon
                        src={sortConfig.asc ? ArrowUpLine : ArrowDownLine}
                        className={atoms({color: 'Text Base'})}
                      />
                    </IconButton>
                  )}
                </Box>
              )}
              {/* Resize handle: call onResizeStart so we measure actual width first */}
              {canRenderResizeHandle && (
                <Box
                  userSelect="none"
                  borderRadius="Medium"
                  padding="4"
                  margin="2"
                  onMouseEnter={() => setHoveredResizeColumnIndex(resizedColumnIndex)}
                  onMouseLeave={() => setHoveredResizeColumnIndex(null)}
                  onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
                    onResizeStart(resizedColumnIndex, e)
                  }
                  {...withShade({
                    className: cx(styles.resizeHandle, {
                      [styles.resizeHandleLeft]: true,
                      [styles.resizeHandleVisible]: shouldShowResizeHandle,
                      [styles.isDragging]: draggingCol === resizedColumnIndex,
                    }),
                    style: {marginLeft: 0.5},
                  })}
                >
                  ||
                </Box>
              )}
              {/* Right-edge handle (opt-in): left-edge handles only resize the *previous* column. */}
              {resizable && lastColumnResizeHandle && index === columns.length - 1 && !col.fixed ? (
                <Box
                  userSelect="none"
                  borderRadius="Medium"
                  padding="4"
                  margin="2"
                  onMouseEnter={() => setHoveredResizeColumnIndex(index)}
                  onMouseLeave={() => setHoveredResizeColumnIndex(null)}
                  onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => onResizeStart(index, e)}
                  {...withShade({
                    className: cx(styles.resizeHandle, {
                      [styles.resizeHandleVisible]:
                        hoveredResizeColumnIndex === index || draggingCol === index,
                      [styles.isDragging]: draggingCol === index,
                    }),
                    style: {marginRight: 0.5},
                  })}
                >
                  ||
                </Box>
              ) : null}
            </Text>
          );
        })}
      </div>
    );
  },
  {tablePart: 'header' as const}
);

const Body = ({
  children,
  className,
  bodyRef,
  ...props
}: {
  children: ReactNode;
  bodyRef?: React.Ref<HTMLDivElement>;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div ref={bodyRef} className={cx(styles.body, className)} {...props}>
    {children}
  </div>
);

// Add a new Rows component that automatically renders sorted data
const Rows = ({
  renderRow,
}: {renderRow: (item: any, index: number) => ReactNode} & Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children'
>) => {
  const ctx = useContext(TableContext);
  if (!ctx) return null;

  const {sortedData} = ctx;

  return (
    <>
      {sortedData.map((item, index) => {
        const rowKey =
          item != null &&
          typeof item === 'object' &&
          'id' in item &&
          (item as {id: unknown}).id != null &&
          (item as {id: unknown}).id !== ''
            ? String((item as {id: unknown}).id)
            : index;
        return <React.Fragment key={rowKey}>{renderRow(item, index)}</React.Fragment>;
      })}
    </>
  );
};

// Add a new GroupedRows component that supports sorting with grouped data
const GroupedRows = ({
  groupedData,
  renderGroupHeader,
  renderRow,
}: {
  groupedData: Record<string, any[]>;
  renderGroupHeader: (groupKey: string, groupData: any[]) => ReactNode;
  renderRow: (item: any, index: number) => ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>) => {
  const ctx = useContext(TableContext);

  // Create a flattened structure that includes group headers
  const flattenedData = useMemo(() => {
    const result: Array<{type: 'group' | 'row'; key: string; data: any; groupKey?: string}> = [];

    // Sort group keys (you can customize this logic)
    const sortedGroupKeys = Object.keys(groupedData).sort((a, b) => {
      // Put "Other" last
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return a.localeCompare(b);
    });

    sortedGroupKeys.forEach((groupKey) => {
      const groupData = groupedData[groupKey];
      if (groupData.length === 0) return;

      // Add group header
      result.push({
        type: 'group',
        key: `group-${groupKey}`,
        data: {groupKey, groupData},
        groupKey,
      });

      // Add rows for this group
      groupData.forEach((item, index) => {
        result.push({
          type: 'row',
          key: `${groupKey}-${item.id || index}`,
          data: item,
          groupKey,
        });
      });
    });

    return result;
  }, [groupedData]);

  // Filter and sort the flattened data based on the current sort configuration
  const sortedFlattenedData = useMemo(() => {
    if (!ctx?.sortConfig) return flattenedData;

    const {sortConfig} = ctx;
    const col = ctx.columns.find((c) => c.type === sortConfig.columnId);

    if (!col?.sort) return flattenedData;
    const sortFunction = col.sort;

    // Separate group headers from rows
    const groupHeaders = flattenedData.filter((item) => item.type === 'group');
    const rows = flattenedData.filter((item) => item.type === 'row');

    // Sort only the rows using the column's sort function
    const sortedRows = [...rows].sort((a, b) => {
      const result = sortFunction(a.data, b.data);
      return sortConfig.asc ? result : -result;
    });

    // Reconstruct the grouped structure with sorted rows
    const result: typeof flattenedData = [];
    const groupMap = new Map<string, typeof rows>();

    // Group the sorted rows by their groupKey
    sortedRows.forEach((row) => {
      const groupKey = row.groupKey;
      if (groupKey && !groupMap.has(groupKey)) {
        groupMap.set(groupKey, []);
      }
      if (groupKey) {
        groupMap.get(groupKey)?.push(row);
      }
    });

    // Add group headers and their sorted rows
    groupHeaders.forEach((header) => {
      const groupKey = header.groupKey;
      const groupRows = groupKey ? groupMap.get(groupKey) || [] : [];

      if (groupRows.length > 0) {
        result.push(header);
        result.push(...groupRows);
      }
    });

    return result;
  }, [ctx, flattenedData]);

  if (!ctx) return null;

  return (
    <>
      {sortedFlattenedData.map((item) => {
        if (item.type === 'group') {
          return (
            <React.Fragment key={item.key}>
              {renderGroupHeader(item.data.groupKey, item.data.groupData)}
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={item.key}>
              {renderRow(item.data, 0)} {/* index is not meaningful in grouped context */}
            </React.Fragment>
          );
        }
      })}
    </>
  );
};

const DragHandlePortal = ({
  rowElement,
  isDragging,
  listeners,
  attributes,
  // sortableId,
  // draggingId,
}: {
  rowElement: HTMLElement | null;
  isDragging: boolean;
  listeners: any;
  attributes: any;
  // sortableId: number;
  // draggingId: number;
}) => {
  const [position, setPosition] = useState<{left: number; top: number} | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!rowElement) return;

    const updatePosition = () => {
      const rect = rowElement.getBoundingClientRect();
      setPosition({
        left: rect.left - 30,
        top: rect.top,
      });
    };

    updatePosition();

    // Set up event listeners
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    // Set up a MutationObserver to detect changes in the DOM
    let timeoutId: number | null = null;
    const debouncedUpdate = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(updatePosition, 100);
    };

    const observer = new MutationObserver(debouncedUpdate);

    // Only observe the row element and its ancestors
    let currentElement: Element | null = rowElement;
    while (currentElement && currentElement !== document.body) {
      observer.observe(currentElement, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
      currentElement = currentElement.parentElement;
    }

    // If dragging, continuously update position using requestAnimationFrame
    if (isDragging) {
      const animate = () => {
        updatePosition();
        animationFrameRef.current = requestAnimationFrame(animate);
      };
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      observer.disconnect();
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [rowElement, isDragging]);

  if (!position) return null;

  return createPortal(
    <Box
      className={styles.handleContainer}
      backgroundColor="transparent"
      position="fixed"
      style={{
        left: position.left,
        top: position.top,
      }}
    >
      <Box
        position="relative"
        color={isDragging ? 'Border Base' : 'Border Subtle'}
        className={cx(styles.dragHandle, {
          [styles.isDragging]: isDragging,
        })}
        style={{
          height: rowElement?.clientHeight,
        }}
        {...listeners}
        {...attributes}
        tabIndex={-1}
      >
        <UIIcon src={DragHandleLine} size="medium" />
      </Box>
    </Box>,
    document.body
  );
};

const Row = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    children?: ReactNode;
    isSelectable?: boolean;
    isSelected?: boolean;
    sortableId?: number;
    expanded?: boolean;
    compactExpandedHeight?: number | null;
    onExpandedChange?: (expanded: boolean) => void;
  }
>(
  (
    {
      children,
      isSelectable,
      isSelected,
      sortableId,
      expanded: expandedProp,
      compactExpandedHeight,
      onExpandedChange,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    const ctx = useContext(TableContext);
    const [rowElement, setRowElement] = useState<HTMLElement | null>(null);
    const [internalExpanded, setInternalExpanded] = useState(false);
    const isExpanded = expandedProp ?? internalExpanded;
    const setIsExpanded = useCallback(
      (expanded: boolean) => {
        if (expandedProp === undefined) {
          setInternalExpanded(expanded);
        }
        onExpandedChange?.(expanded);
      },
      [expandedProp, onExpandedChange]
    );

    const isSortable = ctx?.reorderable && sortableId !== undefined;

    // Add useSortable hook for reorderable rows
    const {attributes, isSorting, listeners, transform, setNodeRef, transition, isDragging} =
      useSortable({
        id: sortableId ?? -1,
        disabled: !isSortable,
      });

    const {onReorderingStart, onReorderingEnd, padding, sortedData} = ctx ?? {};

    useEffect(() => {
      if (isSorting) {
        onReorderingStart?.();
      } else {
        onReorderingEnd?.();
      }
    }, [isSorting, onReorderingStart, onReorderingEnd]);

    if (!ctx) return null;

    const rowStyles = isSortable
      ? {
          transition,
          cursor: isDragging ? 'grabbing' : undefined,
          transform: `translate3d(${transform?.x ?? 0}px, ${transform?.y ?? 0}px, 0)`,
          zIndex: isDragging ? 1 : undefined,
          opacity: isSorting && !isDragging ? 0.7 : 1,
        }
      : {
          display: 'contents',
        };

    return (
      <div
        ref={(node) => {
          setRowElement(node);
          if (isSortable) {
            setNodeRef(node);
          }
          if (typeof ref === 'function') {
            ref(node);
          }
        }}
        className={cx(styles.row, className, {
          [atoms({
            boxShadow: isDragging ? 'Floating' : undefined,
            position: 'relative',
            backgroundColor: 'Background Base',
            borderTop: isDragging ? 'Solid' : undefined,
            borderColor: 'Opacity Surface - State 2',
          })]: isSortable,
          [styles.selectable]: isSelectable,
          [styles.selected]: isSelected,
          [styles.noPadding]: padding === false,
        })}
        style={{...rowStyles, ...style}}
        {...rest}
      >
        {isSortable && (sortedData?.length ?? 0) > 1 && (
          <DragHandlePortal
            rowElement={rowElement}
            isDragging={isDragging}
            // sortableId={sortableId ?? -1}
            // draggingId={active?.id as number}
            listeners={listeners}
            attributes={attributes}
          />
        )}
        {(() => {
          let columnIndex = 0;
          return React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              const currentIndex = columnIndex;
              columnIndex += 1;
              return React.cloneElement(child, {
                columnIndex: currentIndex,
                isExpanded,
                setIsExpanded,
                compactExpandedHeight,
              } as any);
            }
            return child;
          });
        })()}
      </div>
    );
  }
);

const Cell = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    colSpan?: number;
    children?: ReactNode;
    columnIndex?: number;
    isExpanded?: boolean;
    setIsExpanded?: (isExpanded: boolean) => void;
    compactExpandedHeight?: number | null;
    onExpandedChange?: (overflowToggleShown: boolean) => void;
    hasContent?: boolean;
    shadowGradientBackgroundColor?: string;
    compactActions?: ReactNode;
    compactPaddingRight?: number;
    suppressCompactOverflowToggle?: boolean;
    reserveOverflowToggleSpace?: boolean;
    overlayCompactOverflowToggle?: boolean;
    compactOverflowToggleBackdrop?: boolean;
    onCompactContentElementChange?: (element: HTMLDivElement | null) => void;
  }
>(
  (
    {
      children,
      className,
      style,
      colSpan,
      columnIndex,
      isExpanded,
      setIsExpanded,
      compactExpandedHeight,
      onExpandedChange,
      hasContent = true,
      shadowGradientBackgroundColor,
      compactActions,
      compactPaddingRight,
      suppressCompactOverflowToggle = false,
      reserveOverflowToggleSpace = false,
      overlayCompactOverflowToggle = false,
      compactOverflowToggleBackdrop = false,
      onCompactContentElementChange,
      ...rest
    },
    ref
  ) => {
    const ctx = useContext(TableContext);
    const isCompact = ctx?.compact;
    const bodyCellPadding = ctx?.bodyCellPadding ?? 8;

    // Check if this column is hidden
    const columnType = columnIndex !== undefined ? ctx?.columns[columnIndex]?.type : undefined;

    const isHidden = columnType && ctx?.hiddenColumns?.includes(columnType);

    const [showTopShadow, setShowTopShadow] = useState(false);
    const [showBottomShadow, setShowBottomShadow] = useState(false);

    const [isScrollable, setIsScrollable] = useState(false);
    const cellRef = useRef<HTMLDivElement>(null);
    const contentRootRef = useRef<HTMLDivElement>(null);
    const showColumnLabel =
      columnType && ctx?.toggleColumnVisibility
        ? t('tooltip_show_column', {column: t(columnType)})
        : undefined;

    // Handle vertical scroll for shadows
    const handleVerticalScroll = useCallback(() => {
      const element = cellRef.current;
      if (!element || !isCompact) return;

      // Determine scrollability against collapsed height so expand/collapse
      // doesn't briefly drop toggle visibility and cause layout jumps.
      const canScrollWhenCollapsed = element.scrollHeight > MAX_COMPACT_HEIGHT;

      if (isExpanded) {
        setShowTopShadow(false);
        setShowBottomShadow(false);
        setIsScrollable(canScrollWhenCollapsed);
        return;
      }

      const hasTopScroll = element.scrollTop > 0;
      const hasBottomScroll =
        Math.ceil(element.scrollTop) < element.scrollHeight - element.clientHeight;

      setShowTopShadow(hasTopScroll);
      setShowBottomShadow(hasBottomScroll);
      setIsScrollable(canScrollWhenCollapsed);
    }, [isCompact, isExpanded]);

    const cellContent = useMemo(() => {
      if (isHidden) {
        return (
          <Box style={{marginLeft: -4}}>
            {showColumnLabel ? (
              <Tooltip title={showColumnLabel}>
                <IconButton
                  variant="transparent"
                  size="small"
                  aria-label={showColumnLabel}
                  onClick={() => ctx?.toggleColumnVisibility?.(columnType!)}
                >
                  <UIIcon src={ShowLine} className={atoms({color: 'Text Subtle'})} />
                </IconButton>
              </Tooltip>
            ) : hasContent ? (
              <Toggletip
                placement="left"
                title={
                  <Box
                    overflowY="scroll"
                    style={{minWidth: 250, width: 'fit-content', maxHeight: 500}}
                  >
                    {children}
                  </Box>
                }
                src={MoreHorizontalLine}
              />
            ) : null}
          </Box>
        );
      }
      return children;
    }, [children, columnType, ctx, hasContent, isHidden, showColumnLabel]);

    // Check initial scroll state and set up listeners
    useEffect(() => {
      if (!isCompact) return;

      // Initial check for scrollability
      handleVerticalScroll();

      // Set up listeners
      window.addEventListener('resize', handleVerticalScroll);

      // Use ResizeObserver to detect content changes
      const resizeObserver = new ResizeObserver(() => {
        handleVerticalScroll();
      });

      if (cellRef.current) {
        resizeObserver.observe(cellRef.current);
      }

      return () => {
        window.removeEventListener('resize', handleVerticalScroll);
        resizeObserver.disconnect();
      };
    }, [handleVerticalScroll, isCompact, cellContent]);

    const overflowToggleShown =
      !suppressCompactOverflowToggle && Boolean(isScrollable || isExpanded);
    const hasCompactActions = compactActions !== undefined && compactActions !== null;
    const compactActionCount =
      (hasCompactActions ? 1 : 0) +
      (overflowToggleShown || reserveOverflowToggleSpace
        ? overlayCompactOverflowToggle
          ? 0
          : 1
        : 0);
    const compactActionPaddingRight =
      compactActionCount > 0
        ? COMPACT_ACTION_OFFSET +
          compactActionCount * COMPACT_ACTION_BUTTON_SIZE +
          (compactActionCount - 1) * COMPACT_ACTION_GAP +
          COMPACT_ACTION_BUFFER
        : undefined;
    const shouldRenderCompactActionRow = hasCompactActions || overflowToggleShown;
    const resolvedCompactPaddingRight =
      typeof compactPaddingRight === 'number' && typeof compactActionPaddingRight === 'number'
        ? Math.max(compactPaddingRight, compactActionPaddingRight)
        : (compactPaddingRight ?? compactActionPaddingRight);
    const resolvedCompactMaxHeight = isExpanded
      ? 'none'
      : Math.max(compactExpandedHeight ?? MAX_COMPACT_HEIGHT, MAX_COMPACT_HEIGHT);

    // Notify parent when overflow toggle visibility changes
    useEffect(() => {
      onExpandedChange?.(overflowToggleShown);
    }, [overflowToggleShown, onExpandedChange]);

    useEffect(() => {
      onCompactContentElementChange?.(cellRef.current);
      return () => onCompactContentElementChange?.(null);
    }, [onCompactContentElementChange]);

    if (isCompact) {
      return (
        <div
          ref={ref}
          className={cx(styles.cell, className)}
          style={{
            ...(colSpan ? {gridColumn: `span ${colSpan}`} : {}),
            position: 'relative',
            ...style,
            padding: 0,
            height: undefined,
          }}
          {...rest}
        >
          <div
            ref={cellRef}
            style={{
              maxHeight: resolvedCompactMaxHeight,
              overflowY: isExpanded ? 'visible' : 'auto',
              overflowX: 'hidden',
              boxSizing: 'border-box',
              minWidth: 0,
              width: '100%',
              padding: style?.padding ?? bodyCellPadding,
              paddingInlineEnd: resolvedCompactPaddingRight,
              height: style?.height ?? '100%',
            }}
            onScroll={handleVerticalScroll}
          >
            <div
              className={cx(styles.verticalShadowOverlay, styles.topShadow, {
                [styles.visible]: showTopShadow && !isExpanded,
              })}
              style={{
                background: shadowGradientBackgroundColor
                  ? `linear-gradient(to bottom, ${shadowGradientBackgroundColor} 20%, transparent)`
                  : undefined,
              }}
            />
            <div
              className={cx(styles.verticalShadowOverlay, styles.bottomShadow, {
                [styles.visible]: showBottomShadow && !isExpanded,
              })}
              style={{
                background: shadowGradientBackgroundColor
                  ? `linear-gradient(to top, ${shadowGradientBackgroundColor} 20%, transparent)`
                  : undefined,
              }}
            />
            {shouldRenderCompactActionRow && (
              <div className={styles.compactActionRow}>
                {compactActions}
                {overflowToggleShown && (
                  <IconButton
                    variant="borderless"
                    size="small"
                    className={cx({
                      [styles.compactOverflowToggleBackdrop]: compactOverflowToggleBackdrop,
                    })}
                    onClick={() => {
                      setIsExpanded?.(!isExpanded);
                    }}
                    style={{height: COMPACT_ACTION_BUTTON_SIZE}}
                  >
                    <UIIcon
                      src={isExpanded ? UploadLine : ExpandDownLine}
                      size="small"
                      className={atoms({color: 'Text Base'})}
                    />
                  </IconButton>
                )}
              </div>
            )}
            <div ref={contentRootRef} data-compact-content-root="true">
              {cellContent}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cx(styles.cell, className)}
        style={{
          ...style,
          ...(colSpan ? {gridColumn: `span ${colSpan}`} : {}),
          padding: style?.padding ?? bodyCellPadding,
        }}
        {...rest}
      >
        {cellContent}
      </div>
    );
  }
);

const Loading = ({
  rowCount = 15,
  className,
  style,
  firstCellPaddingLeft,
  ...rest
}: {
  rowCount?: number;
  firstCellPaddingLeft?: number;
} & React.HTMLAttributes<HTMLDivElement>) => {
  const ctx = useContext(TableContext);
  if (!ctx) return null;

  const {columns, hiddenColumns, padding} = ctx;

  const renderSkeletonCell = (column: Column<any>) => {
    const isHidden = hiddenColumns?.includes(column.type);

    if (isHidden) {
      return <UIIcon src={MoreHorizontalLine} size="small" />;
    }

    // If a custom skeleton function is provided, use it
    if (column.skeleton) {
      return column.skeleton();
    }

    const fallbackSkeletonWidth = Math.max(
      80,
      Math.min(column.width ?? column.minWidth ?? 330, 330) - 24
    );

    return (
      <Skeleton.Text
        width={fallbackSkeletonWidth as React.ComponentProps<typeof Skeleton.Text>['width']}
      />
    );
  };

  return (
    <>
      {Array.from({length: rowCount}).map((_, rowIndex) => (
        <Row
          key={rowIndex}
          className={className}
          style={{...style, opacity: 1 - rowIndex * 0.06}}
          {...rest}
        >
          {columns.map((column, index) => (
            <Cell
              key={column.type}
              className={padding ? undefined : styles.noPadding}
              style={{
                opacity: 1 - rowIndex * 0.06,
                paddingLeft: index === 0 ? firstCellPaddingLeft : undefined,
              }}
            >
              {renderSkeletonCell(column)}
            </Cell>
          ))}
        </Row>
      ))}
    </>
  );
};

export const Table = Object.assign(TableComponent, {
  Header,
  Body,
  Row,
  Cell,
  Rows,
  GroupedRows,
  Loading,
});
